import { Card, Col, Descriptions, Row, Space, Table, Typography } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { usePermissions } from '../../auth/store';
import { formatCoins, formatRupees } from '../../lib/money';
import { ListenerName } from '../../lib/listenerName';
import { minutes, useConsoleMetrics } from '../dashboard/DashboardPage';
import { formatDuration, RANGE_PRESETS } from '../listeners/listenerStats';
import { StatsRangeFilter, useUrlStatsRange } from '../listeners/ListenerStatsWidgets';
import { listenerPerformance, type ListenerPerformanceRow } from '../reports/reports';

/**
 * Breakdowns for ONE selected period — calls, listener performance and money —
 * from the same figures the dashboard and the reports use. Sits above the
 * rolling hourly / daily charts, which keep their own windows.
 */
export function PeriodBreakdown() {
  const { can } = usePermissions();
  const { preset, setPreset, setCustom, range } = useUrlStatsRange('last7');
  const periodLabel = RANGE_PRESETS.find((p) => p.value === preset)?.label ?? 'Selected period';
  const metrics = useConsoleMetrics(range);
  const m = metrics.data;
  const canListeners = can('listeners.view');

  // Top listeners of the period by connected talk time.
  const performance = useQuery({
    queryKey: ['admin', 'analytics', 'listener-performance', range],
    queryFn: async () =>
      (await listenerPerformance(range!, 500)).rows
        .filter((r) => r.calls > 0)
        .sort((a, b) => b.totalSeconds - a.totalSeconds)
        .slice(0, 10),
    enabled: range !== null && canListeners,
  });

  return (
    <Card
      title={`Period breakdown · ${periodLabel}`}
      extra={<StatsRangeFilter preset={preset} setPreset={setPreset} setCustom={setCustom} />}
      loading={metrics.isLoading}
    >
      {m && (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          <Row gutter={[16, 16]}>
            <Col xs={24} lg={12}>
              <Descriptions title="Calls" size="small" bordered column={{ xs: 1, sm: 2 }}>
                <Descriptions.Item label="Completed">{m.calls.completed.toLocaleString('en-IN')}</Descriptions.Item>
                <Descriptions.Item label="Missed">{m.calls.missed.toLocaleString('en-IN')}</Descriptions.Item>
                <Descriptions.Item label="Declined">{m.calls.declined.toLocaleString('en-IN')}</Descriptions.Item>
                <Descriptions.Item label="Failed">{m.calls.failed.toLocaleString('en-IN')}</Descriptions.Item>
                <Descriptions.Item label="Audio minutes">{minutes(m.calls.audioSeconds)}</Descriptions.Item>
                <Descriptions.Item label="Video minutes">{minutes(m.calls.videoSeconds)}</Descriptions.Item>
                <Descriptions.Item label="Total minutes" span={2}>
                  <strong>{minutes(m.calls.totalSeconds)}</strong> across {m.calls.total.toLocaleString('en-IN')} calls
                </Descriptions.Item>
              </Descriptions>
            </Col>
            <Col xs={24} lg={12}>
              <Descriptions title="Financial" size="small" bordered column={{ xs: 1, sm: 2 }}>
                <Descriptions.Item label="Recharge amount">{formatRupees(m.money.rechargePaise)}</Descriptions.Item>
                <Descriptions.Item label="Coins purchased">{formatCoins(m.money.coinsPurchased)}</Descriptions.Item>
                <Descriptions.Item label="Listener earnings">{formatRupees(m.money.listenerEarningsPaise)}</Descriptions.Item>
                <Descriptions.Item label="Platform margin">{formatRupees(m.money.platformMarginPaise)}</Descriptions.Item>
                <Descriptions.Item label="Avg revenue per call" span={2}>
                  {m.money.avgRevenuePerCallPaise === null ? '—' : formatRupees(m.money.avgRevenuePerCallPaise)}
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    {' '}
                    (recharge amount ÷ completed calls)
                  </Typography.Text>
                </Descriptions.Item>
              </Descriptions>
            </Col>
          </Row>
          {canListeners && (
            <Table<ListenerPerformanceRow>
              size="small"
              rowKey="id"
              title={() => <strong>Listener performance — top 10 by talk time</strong>}
              loading={performance.isLoading}
              dataSource={performance.data ?? []}
              pagination={false}
              scroll={{ x: 'max-content' }}
              locale={{ emptyText: 'No listener took a call in this period' }}
              columns={[
                {
                  title: 'Listener',
                  render: (_: unknown, r) => (
                    <ListenerName
                      listener={{ displayName: r.displayName, handle: r.handle, id: r.id }}
                      listenerId={r.id}
                      secondary="inline"
                    />
                  ),
                },
                { title: 'Calls', dataIndex: 'calls', align: 'right' },
                { title: 'Audio', align: 'right', render: (_: unknown, r) => minutes(r.audioSeconds) },
                { title: 'Video', align: 'right', render: (_: unknown, r) => minutes(r.videoSeconds) },
                { title: 'Total', align: 'right', render: (_: unknown, r) => <strong>{minutes(r.totalSeconds)}</strong> },
                {
                  title: 'Online time',
                  align: 'right',
                  render: (_: unknown, r) => (r.onlineSeconds === null ? '—' : formatDuration(r.onlineSeconds)),
                },
                {
                  title: 'Earnings',
                  align: 'right',
                  render: (_: unknown, r) => (r.earningsPaise === null ? '—' : formatRupees(r.earningsPaise)),
                },
              ]}
            />
          )}
        </Space>
      )}
    </Card>
  );
}
